import React, { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCreateRoyaltyReminder } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { SelectGamesDialog } from "../core/SelectGamesDialog";
import { RoyaltyReminderPublisherSelect } from "./RoyaltyReminderPublisherSelect";

/** @type {{ label: string, value: import("@seabrookstudios/pitch2table-core").CycleType }[]} */
const cycleOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-yearly", value: "twice-yearly" },
  { label: "Yearly", value: "yearly" },
];

export const AddRoyaltyReminderDialog = () => {
  const createReminder = useCreateRoyaltyReminder();

  const [cycleType, setCycleType] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").CycleType} */ ("monthly"),
  );
  const [startDate, setStartDate] = useState(DateTime.now().toMillis());
  const [gameIds, setGameIds] = useState(/** @type {import("@seabrookstudios/types").GameId[]} */ ([]));
  const [publisherIds, setPublisherIds] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId[]} */ ([]),
  );

  const onSaveHandler = async () => {
    await createReminder.mutateAsync({
      cycleType,
      startDate,
      gameIds,
      publisherIds,
    });
  };

  const canSave =
    createReminder.isPending || !cycleType || !startDate || gameIds.length === 0 || publisherIds.length === 0;

  return (
    <FullScreenDialog
      id="add-royalty-reminder"
      title="Add Royalty Reminder"
      actionText="CREATE"
      onSaveHandler={onSaveHandler}
      canSave={canSave}
      isBusy={createReminder.isPending}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cycle Type
          </Typography>
          <ButtonGroup variant="outlined" disableElevation fullWidth>
            {cycleOptions.map((option) => (
              <Button
                key={option.value}
                variant={cycleType === option.value ? "contained" : "outlined"}
                onClick={() => setCycleType(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <Stack direction="column">
          <DatePicker
            value={DateTime.fromMillis(startDate)}
            label="Start date"
            onChange={(dateValue) => {
              if (!dateValue) {
                return;
              }
              setStartDate(dateValue.toMillis());
            }}
          />
          <Typography color="text.secondary" variant="caption" sx={{ marginTop: "3px", marginLeft: "14px" }}>
            The start date used to calculate the reminder cycle.
          </Typography>
        </Stack>
        <SelectGamesDialog value={gameIds} onChange={setGameIds} defaultSelectedStatuses={["Published", "Signed"]} />
        <RoyaltyReminderPublisherSelect gameIds={gameIds} value={publisherIds} onChange={setPublisherIds} />
      </Stack>
    </FullScreenDialog>
  );
};
